import { useCallback, useEffect, useRef, useState } from 'react';

const MUTE_KEY = 'agentCharacter.muted';

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

/**
 * Drives AgentCharacter's voice. Two backends, chosen per company (services/tts/
 * ttsSettings.js) via `fetchStatus`:
 *  - 'browser' (default, no config needed) — the Web Speech API. There's no accessible
 *    audio buffer for this API (it plays through the OS engine directly), so real
 *    amplitude-based lip-sync isn't possible here; `mouthOpen` flips on each `boundary`
 *    event (fired per word) instead — good enough to look "alive".
 *  - 'google' | 'elevenlabs' — `synthesize(text)` fetches real audio from this app's
 *    backend (the provider key never reaches the browser). Real audio means real
 *    amplitude analysis is possible: a Web Audio AnalyserNode drives `mouthOpen` from
 *    actual volume, which is why this path exists at all rather than just always using
 *    the free browser voice.
 *
 * @param {{fetchStatus?: () => Promise<object>, synthesize?: (text: string) => Promise<{audioBase64: string, mimeType: string}>}} opts
 *   Both are injected by the caller (ChatPanel.jsx uses the authenticated fetchApi,
 *   EmbedChat.jsx uses plain fetch with its key/agentKey) so this hook stays agnostic of
 *   which auth style is in play. Omit both to always use the browser voice.
 */
export function useCharacterSpeech({ fetchStatus, synthesize } = {}) {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [speaking, setSpeaking] = useState(false);
  const [mouthOpen, setMouthOpen] = useState(false);
  const [muted, setMuted] = useState(() => (
    typeof window !== 'undefined' && window.localStorage.getItem(MUTE_KEY) === '1'
  ));
  const statusRef = useRef(null);
  // Whether the company configured a server-side STT provider — drives the
  // Composer's server-dictation fallback where the browser has no built-in
  // speech recognition. State (not just the ref) so toggling it re-renders.
  const [serverSttReady, setServerSttReady] = useState(false);
  const audioElRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  // Fetched once per mount, deliberately not re-run when `fetchStatus`'s identity
  // changes on later renders (matches this codebase's existing fetchApi-in-effect
  // convention, e.g. AgentDetail.jsx's ApiKeyControl) — an admin changing voice
  // settings mid-session applies on this character's next mount, not live, same as
  // this app's other once-loaded config (agent list, modules).
  useEffect(() => {
    if (!fetchStatus) return;
    fetchStatus().then((s) => {
      statusRef.current = s;
      setServerSttReady(!!(s && s.sttProvider && s.sttProvider !== 'browser'));
    }).catch(() => {}); // no status = just use the browser voice
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      window.localStorage.setItem(MUTE_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  const stopRemote = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current.onplay = null;
      audioElRef.current.onended = null;
      audioElRef.current.onerror = null;
      audioElRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    stopRemote();
    if (supported) window.speechSynthesis.cancel();
    setSpeaking(false);
    setMouthOpen(false);
  }, [supported, stopRemote]);

  // Cancelling here (rather than inside toggleMuted) keeps `cancel` itself stable
  // and avoids a stale-closure race between the state update and stopping playback.
  useEffect(() => {
    if (muted) cancel();
  }, [muted, cancel]);

  const getAudioContext = () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  };

  /** Plays a base64 audio reply and drives `mouthOpen` from its actual volume via a
   *  Web Audio AnalyserNode — the real amplitude-driven lip-sync browser TTS can't do. */
  const playRemote = (audioBase64, mimeType) => {
    const audioEl = new Audio(`data:${mimeType};base64,${audioBase64}`);
    audioElRef.current = audioEl;
    const ctx = getAudioContext();
    const source = ctx.createMediaElementSource(audioEl);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyser.connect(ctx.destination);
    // Time-domain (waveform) data, not frequency-domain: verified against a synthetic
    // test tone that frequency-bin averaging under-reports loudness for anything with
    // energy concentrated in a few bins (a single-frequency tone averaged to ~9/255
    // even at full scale, while its one loud bin peaked at 246/255) — real speech is
    // broadband so it's less extreme there, but average absolute deviation from the
    // 128 silence baseline is the direct, robust "how loud right now" signal either way.
    const data = new Uint8Array(analyser.fftSize);

    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += Math.abs(data[i] - 128);
      setMouthOpen(sum / data.length > 4); // silence/background noise stays closed
      rafRef.current = requestAnimationFrame(tick);
    };
    audioEl.onplay = () => { setSpeaking(true); tick(); };
    audioEl.onended = () => { stopRemote(); setSpeaking(false); setMouthOpen(false); };
    audioEl.onerror = () => { stopRemote(); setSpeaking(false); setMouthOpen(false); };
    return audioEl.play().catch(() => { setSpeaking(false); }); // autoplay-blocked or decode error
  };

  const speakBrowser = (text) => {
    if (!supported) return;
    const utterance = new SpeechSynthesisUtterance(text);
    const s = statusRef.current;
    if (s) {
      utterance.rate = clamp(s.rate ?? 1, 0.1, 10);
      utterance.pitch = clamp(1 + (s.pitch ?? 0) * 0.5, 0, 2);
    }
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => { setSpeaking(false); setMouthOpen(false); };
    utterance.onerror = () => { setSpeaking(false); setMouthOpen(false); };
    utterance.onboundary = () => setMouthOpen((prev) => !prev);
    window.speechSynthesis.speak(utterance);
  };

  const speak = useCallback(async (text) => {
    if (muted || !text) return;
    cancel(); // barge-in: don't overlap with a prior reply

    const s = statusRef.current;
    const provider = s && synthesize && s.activeProvider;
    let remoteReady = false;
    if (provider === 'google') remoteReady = s.hasGoogleKey;
    else if (provider === 'elevenlabs') remoteReady = s.hasElevenLabsKey;
    else if (provider === 'sarvam') remoteReady = s.hasSarvamKey;

    if (remoteReady) {
      try {
        const audio = await synthesize(text);
        if (audio && audio.audioBase64) {
          await playRemote(audio.audioBase64, audio.mimeType || 'audio/mpeg');
          return;
        }
      } catch {
        // provider call failed (network blip, key revoked after status was cached, etc.)
        // — fall through to the free browser voice rather than staying silent.
      }
    }
    speakBrowser(text);
  }, [muted, synthesize, cancel]);

  useEffect(() => cancel, [cancel]);

  return { supported: supported || !!synthesize, speaking, mouthOpen, muted, toggleMuted, speak, cancel, serverSttReady };
}

export default useCharacterSpeech;
